import type React from 'react';

import { usePagination } from '../../hooks/usePagination';
import { translate } from '../../services/i18n';
import { PaginationService } from '../../services/pagination';
import { Pagination } from '../Pagination';

import {
  StyledTableHeader,
  StyledTableBody,
  StyledTableItem,
  StyledPaginationWrapper,
  StyledTableHeaderItem,
} from './styles';

const PAGE_SIZE = 5;

interface TableHeaderConfig {
  translateKey: string;
  width?: string;
}

interface TableProps<Item> {
  keyProperty: keyof Item;
  data: Item[];
  onPageChange?: (page: number) => void;
  RowComponent: React.FC<{ item: Item }>;
  pageSize?: number;
  headerConfig: TableHeaderConfig[];
}

export const Table = <Item extends object>({
  keyProperty,
  data,
  onPageChange: onPageChangeProp,
  RowComponent,
  pageSize = PAGE_SIZE,
  headerConfig,
}: TableProps<Item>) => {
  const { currentPage, itemsToShow, onPageChange } = usePagination({
    items: data,
    pageSize,
  });

  const handlePageChange = (page: number) => {
    onPageChange(page);
    onPageChangeProp?.(page);
  };

  const totalCount = data?.length || 0;

  const emptyItems = PaginationService.createEmptySlots({
    totalItemsCount: totalCount,
    pageSize: pageSize,
  });

  const lastPage = Math.ceil(totalCount / pageSize);
  const showPagination = pageSize < totalCount;

  return (
    <>
      <StyledTableHeader>
        {headerConfig.map(({ translateKey, width }, index) => (
          <StyledTableHeaderItem key={translateKey} width={width}>{`${translate(translateKey)}${
            index === 0 ? ` (${totalCount})` : ''
          }`}</StyledTableHeaderItem>
        ))}
      </StyledTableHeader>
      <StyledTableBody showPagination={showPagination}>
        {itemsToShow.map((item) => (
          <StyledTableItem key={item[keyProperty] as string}>
            <RowComponent item={item} />
          </StyledTableItem>
        ))}
        {showPagination && currentPage === lastPage
          ? emptyItems.map((key) => <StyledTableItem key={`empty-${key}`} />)
          : null}
      </StyledTableBody>
      {showPagination ? (
        <StyledPaginationWrapper>
          <Pagination
            currentPage={currentPage}
            itemsCount={totalCount}
            handlePageChange={handlePageChange}
            itemsPerPage={pageSize}
          />
        </StyledPaginationWrapper>
      ) : null}
    </>
  );
};
